import React from 'react';
import { sb } from '../../../modules/state';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ProtocolPage } from './ProtocolPage';
import { Modal } from '../../ui/Modal';

export function ProtocolTMAW() {
  const { currentUser, isSubscribed } = useAuth();
  const navigate = useNavigate();
  const [isAbandonModalOpen, setIsAbandonModalOpen] = React.useState(false);
  const [activeFast, setActiveFast] = React.useState(null);
  const [stopReason, setStopReason] = React.useState('Hunger');

  // Check for active fast on mount
  React.useEffect(() => {
    const checkActiveFast = async () => {
      if (!sb || !currentUser?.id) return;

      try {
        const { data } = await sb
          .from('fasting_sessions')
          .select('*')
          .eq('user_id', currentUser.id)
          .eq('status', 'active')
          .single();
        setActiveFast(data);
      } catch (error) {
        // No active fast found, which is fine
      }
    };

    checkActiveFast();
  }, [currentUser]);

  const handleStartProtocol = async () => {
    if (!sb || !currentUser?.id) {
      console.error('[ProtocolTMAW] No user or Supabase');
      return;
    }

    // Check if there's an active fast
    if (activeFast) {
      setIsAbandonModalOpen(true);
      return;
    }

    try {
      const today = new Date().toISOString().split('T')[0];
      await sb.from('profiles').upsert({
        id: currentUser.id,
        current_protocol: 'TMAW',
        protocol_start_date: today,
      });
      console.log('[ProtocolTMAW] Protocol started: TMAW');
      navigate('/dashboard');
    } catch (error) {
      console.error('[ProtocolTMAW] Error starting protocol:', error);
    }
  };

  const handleConfirmAbandon = async () => {
    if (!sb || !currentUser?.id || !activeFast) {
      setIsAbandonModalOpen(false);
      return;
    }

    try {
      // Mark current fast as abandoned
      await sb.from('fasting_sessions').update({
        fast_end: new Date().toISOString(),
        status: 'abandoned',
        reason: stopReason,
      }).eq('id', activeFast.id);

      // Switch protocol
      const today = new Date().toISOString().split('T')[0];
      await sb.from('profiles').upsert({
        id: currentUser.id,
        current_protocol: 'TMAW',
        protocol_start_date: today,
      });

      setActiveFast(null);
      setIsAbandonModalOpen(false);
      console.log('[ProtocolTMAW] Fast abandoned, protocol set to TMAW');
      navigate('/dashboard');
    } catch (error) {
      console.error('[ProtocolTMAW] Error abandoning fast:', error);
    }
  };

  const biochemistryList = (
    <ul className="list-disc list-inside space-y-1">
      <li>Hormetic stress response — antioxidant and DNA repair genes upregulated</li>
      <li>Stem cell activation — immune regeneration observed at 48–72 hours</li>
      <li>IGF-1 significantly reduced — anti-aging and protective reset</li>
      <li>Deep ketosis — brain running efficiently on ketones</li>
    </ul>
  );

  const handleSubscribe = () => {
    const email = currentUser?.email || '';
    const freeTrialUrl = 'https://222fasting.lemonsqueezy.com/checkout/buy/cc5bb239-4095-4549-87c5-70a900626c44?embed=1&checkout[email]=' + encodeURIComponent(email);
    window.LemonSqueezy?.Url?.Open(freeTrialUrl);
  };

  return (
    <>
      <ProtocolPage
        protocolName="TMAW"
        tagline="Sustainable Deep Reset"
        tierBadge="Paid"
        whatIs="Two Meals A Week. 48–72 hour fasting windows. The body enters deep metabolic reset territory."
        biochemistry={biochemistryList}
        whoItIsFor="Users with paid access who have completed OMAD stabilisation. Recommended when insulin is 10–15 mIU/L range."
        francoisSays="TMAW is the protocol most of my clients live in long term. It is hard enough to create real change and sustainable enough to continue."
        ctaText="Select Protocol"
        isLocked={!isSubscribed}
        lockedMessage="TMAW requires a 222fasting subscription"
        lockedCtaText="Start Free Trial"
        onCtaClick={handleSubscribe}
      />

      {/* Abandon Modal */}
      <Modal
        isOpen={isAbandonModalOpen}
        onClose={() => setIsAbandonModalOpen(false)}
        title="Are you ending your fast early?"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-on-surface-variant mb-2">
              Reason:
            </label>
            <select
              value={stopReason}
              onChange={(e) => setStopReason(e.target.value)}
              className="w-full rounded-lg border border-outline bg-surface px-3 py-2 text-on-surface focus:border-primary focus:outline-none"
            >
              <option>Hunger</option>
              <option>Medical</option>
              <option>Social</option>
              <option>Personal</option>
              <option>Other</option>
            </select>
          </div>

          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => setIsAbandonModalOpen(false)}
              className="flex-1 rounded-lg bg-surface-container px-4 py-2 text-base font-medium text-on-surface hover:bg-surface-container-hover"
            >
              Continue Fasting
            </button>
            <button
              onClick={handleConfirmAbandon}
              className="flex-1 rounded-lg bg-error-container px-4 py-2 text-base font-medium text-error hover:bg-error-container/80"
            >
              Confirm Stop
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
